<template>
    <div class="cz2__steps-option cz2__steps-options__shared">
        <step-header
            :hasSubMenu="false"
            :showSubMenuTitle="true"
            :subMenuTitle="canBuyPop && priced ? `${priced.priceFormatted} ${$t('EACH')}` : ''"
            :showColorSwatches="false"
            :hasPrice="true"
        />

        <swiper
            topQuerySelector=".cz2__pack"
            selectedQuerySelector=".selection-card--selected"
            :class="{ 'is-kiosk': isKiosk }"
            class="cz2__packs cz2__steps-options__list-wrapper"
        >
            <ul
                ref="selectionCardList"
                :class="{
                    'cz2__scrollbar-margin': isKiosk && hasScrollbar,
                    'cz2__is-kiosk': isKiosk,
                }"
                class="cz2__scrollbar-hide selection-card-list"
            >
                <selection-card-multi
                    v-for="pack in packsSorted"
                    :key="pack.code"
                    :itemDescription="getDescription(pack)"
                    :item="pack"
                    :inspectOverlay="true"
                    :isAttachPack="true"
                    :large="isSpellingBee"
                    @add="addPack(pack)"
                    @quantityAdd="quantityAdd(pack)"
                    @quantitySubtract="quantitySubtract(pack)"
                    class="cz2__pack"
                />
            </ul>
            <div class="cz2__sb-copyright">
                {{ $t('WB_COPYRIGHT') }}
            </div>
        </swiper>
    </div>
</template>

<style lang="scss">
    @import "../style/variables.scss";

    #cz2.cz2 {
        .cz2__packs {
            .selection-card-list {
                padding-top: 8px;
                grid-row-gap: 40px;

                &.cz2__is-kiosk {
                    padding-left: calc(2 * var(--layout-spacing));
                    padding-right: calc(2 * var(--layout-spacing));
                }
            }
        }

        .cz2__sb-copyright {
            display: none;
        }
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //   ▗▄▄▖▗▄▄▖ ▗▄▄▄▖▗▖   ▗▖   ▗▄▄▄▖▗▖  ▗▖ ▗▄▄▖    ▗▄▄▖ ▗▄▄▄▖▗▄▄▄▖
    //  ▐▌   ▐▌ ▐▌▐▌   ▐▌   ▐▌     █  ▐▛▚▖▐▌▐▌       ▐▌ ▐▌▐▌   ▐▌
    //   ▝▀▚▖▐▛▀▘ ▐▛▀▀▘▐▌   ▐▌     █  ▐▌ ▝▜▌▐▌▝▜▌    ▐▛▀▚▖▐▛▀▀▘▐▛▀▀▘
    //  ▗▄▄▞▘▐▌   ▐▙▄▄▖▐▙▄▄▖▐▙▄▄▖▗▄█▄▖▐▌  ▐▌▝▚▄▞▘    ▐▙▄▞▘▐▙▄▄▖▐▙▄▄
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    #cz2.cz2__is-sb {
        .cz2__packs {
            .selection-card-list {
                display: grid;
                grid-template-columns: repeat(2, minmax(0, 1fr));
                gap: var(--layout-spacing);

                margin-left: auto;
                margin-right: auto;

                max-width: 90%;

                @include respond-below(sm) {
                    margin-top: 5px;
                }
            }
        }

        .cz2__sb-copyright {
            display: block;
            font-size: 12px;
            font-family: 'Proxima Nova', sans-serif;
            color: color(sbDarkGold);
            margin-left: auto;
            margin-right: auto;
            max-width: 90%;
            padding: 0 16px 16px 16px;

            @include respond-above(sm) {
                font-size: 16px;
            }
        }
    }

    #cz2.cz2__is-sb.cz2__is-kiosk {
        .cz2__packs {
            .selection-card-list {
                max-width: 90%;
            }
        }
    }
</style>

<script>
    import Swiper from './ui/Swiper.vue';
    import StepHeader from './ui/StepHeader.vue';
    import SelectionCardMulti from './ui/SelectionCardMulti.vue';

    import * as constants from '../constants';

    import { formatPrice } from '../helpers/formatPrice';

    export default {
        components: {
            StepHeader,
            Swiper,
            SelectionCardMulti,
        },

        data() {
            return {
                currency: 'USD',
            };
        },

        computed: {
            options() {
                return this.$store.state.customizer.options;
            },

            canBuyPop() {
                return this.options && this.options.canBuyPop;
            },

            isKiosk() {
                return this.$store.state.customizer.options.isKiosk;
            },

            viewController() {
                return this.$store.state.customizer.viewController;
            },

            packs() {
                return this.$store.getters.attachPacks.map((p) => {
                    const pack = { ...p };

                    pack.priceFormatted = this.formatPrice(pack.price);

                    return pack;
                });
            },

            priced() {
                // Find a pack with non-zero price.
                const priced = this.packs.find((x) => x.price && x.price > 0);

                if (priced) {
                    return priced;
                }

                return this.packs[0];
            },

            packsSorted() {
                const inStockComponents = this.packs.filter((pack) => !this.isOutOfStock(pack.component));
                const oosComponents = this.packs.filter((pack) => this.isOutOfStock(pack.component));

                return inStockComponents.concat(oosComponents);
            },

            attachPackAddComponent() {
                return this.$store.state.attachPackAddComponent;
            },

            isSpellingBee() {
                return this.$store.state.isSpellingBee;
            },

            spellingBeeType() {
                return this.$store.state.spellingBeeType;
            },

            hasScrollbar() {
                return this.$store.state.hasScrollbar;
            },
        },

        watch: {
            attachPackAddComponent(newValue) {
                if (newValue !== null) {
                    this.addPack(newValue);
                }
            },
        },

        mounted() {
            this.updateNavigation();
            this.getSnapshot();
        },

        methods: {
            addPack(pack) {
                const toAdd = pack;

                // Can we even add one.
                // eslint-disable-next-line
                const total = this.packs.reduce((count, pack) => count + pack.quantity, 0);

                if (total >= constants.ATTACH_PACKS_LIMIT) {
                    return;
                }

                // Check if we already know about this one.
                let added = false;

                this.viewController.state.forEach((s) => {
                    if (s.placement.code === constants.PLACEMENT_AP_PREVIEW) {
                        return;
                    }

                    if (s.placement.code === constants.PLACEMENT_AP_PREVIEW_SECONDARY) {
                        return;
                    }

                    if (s.component.placementIndependentCode === toAdd.placementIndependentCode) {
                        if (this.viewController.isAvailable(s.component, s.component.quantityMultiplier + 1)) {
                            this.$store.dispatch('bumpVersion');

                            this.$store.dispatch('removeAlertFromPlacement', s.placement.code);

                            this.$store.dispatch('setComponentQuantity', {
                                component: s.component,
                                quantity: s.component.quantityMultiplier + 1,
                            });

                            this.$store.dispatch('syncAttachPacks');
                        }

                        added = true;
                    }
                });

                if (!added) {
                    let firstFree = null;

                    this.viewController.state.forEach((s) => {
                        if (constants.PLACEMENT_PACKS.includes(s.placement.code)) {
                            if (s.component.placeholder) {
                                if (firstFree == null) {
                                    firstFree = s.placement;
                                }
                            }
                        }
                    });

                    if (firstFree) {
                        const select = firstFree.components.find((c) => c.placementIndependentCode === toAdd.placementIndependentCode
                            && this.viewController.isAvailable(c));

                        if (select) {
                            this.$store.dispatch('bumpVersion');

                            this.$store.dispatch('removeAlertFromPlacement', firstFree.code);

                            this.viewController.updateComponent(firstFree.code, select.code);

                            const setQuantity = this.viewController.selectedAtPlacement(firstFree.code);

                            this.$store.dispatch('setComponentQuantity', {
                                component: setQuantity,
                                quantity: 1,
                            });

                            this.$store.dispatch('syncAttachPacks');
                        }
                    }
                }

                this.$store.dispatch('setAttachPackAddComponent', null);
            },

            quantityAdd(pack) {
                this.viewController.state.forEach((s) => {
                    if (s.component.placementIndependentCode === pack.placementIndependentCode) {
                        this.$store.dispatch('bumpVersion');

                        this.$store.dispatch('setComponentQuantity', {
                            component: s.component,
                            quantity: s.component.quantityMultiplier + 1,
                        });

                        this.$store.dispatch('syncAttachPacks');
                    }
                });
            },

            quantitySubtract(pack) {
                this.viewController.state.forEach((s) => {
                    if (s.component.placementIndependentCode === pack.placementIndependentCode) {
                        this.$store.dispatch('bumpVersion');

                        this.$store.dispatch('setComponentQuantity', {
                            component: s.component,
                            quantity: s.component.quantityMultiplier - 1,
                        });

                        if (s.component.quantityMultiplier === 0) {
                            const empty = s.placement.components.find((c) => c.placeholder);

                            this.viewController.updateComponent(s.placement.code, empty.code);
                        }

                        this.$store.dispatch('syncAttachPacks');
                    }
                });
            },

            isNew(pack) {
                return pack.component && (pack.component.custom?.['fun-new'] === 'true' || pack.component.custom?.['fun-new-default'] === 'true');
            },

            isOutOfStock(component) {
                return this.viewController.isAllowed(component) && !this.viewController.isAvailable(component);
            },

            async getSnapshot() {
                const popSnapshot = await this.$store.dispatch('snapshot', 'SnapshotNarrow');

                this.$store.dispatch('setAttachPacksPreviewImage', popSnapshot);
            },

            updateNavigation() {
                this.$emit('updateNavigation', {
                    hasNext: true,
                    nextSpecial: 'Name',
                    canGoNext: true,
                    canGoBack: true,
                });
            },

            // TODO: These all will need to be consolidated later. Copy-pasting for now since there is
            // already a precedent. DRY later.
            formatPrice(price) {
                const v = this.priceValue(price);

                if (v == null || v === 0) {
                    return this.$t('ITEM_FREE');
                }

                return formatPrice(this.$store.state, v.value, 'compact');
            },

            priceValue(price) {
                if (price == null) {
                    return 0;
                }

                if (typeof price === 'number') {
                    return { value: price };
                }

                return price.find((p) => p.currencyCode === this.currency);
            },

            getDescription(pack) {
                return this.$t('TOTAL_ADDED').replace('{0}', pack?.component?.description?.[0]?.name ?? this.$t('PACK'));
            },
        },
    };
</script>
